import * as React from 'react';

/** The anchored text itself, which reopens rather than dismisses. */
const COMMENT_MARK_SELECTOR = '.cotera-comment';

/**
 * Anything a comment surface opened that does not live inside its DOM.
 *
 * Radix renders menus, dialogs and tooltips through a portal on `body`, so a press on
 * the card's own overflow menu is nowhere near the card as far as `contains` is
 * concerned. Treating that as a press outside dismissed the card in the same gesture
 * that opened one of its actions — which is why Edit appeared to do nothing while
 * Delete, whose work is done by the time the card goes, appeared to work.
 */
const PORTALLED_SURFACE_SELECTOR =
  '[data-radix-popper-content-wrapper],[data-slot="dropdown-menu-content"],[role="menu"],[role="dialog"]';

/**
 * Calls back when a press lands outside the open comment surface.
 *
 * Every comment surface opens off the caret sitting inside a `comment` mark, which
 * means clicking elsewhere in the prose closes it for free — the caret moves and the
 * mark is no longer active. What that leaves uncovered is a press that changes no
 * selection at all: the margin beside the text, the padding under the document, the
 * page around the editor. Those are the clicks this is for.
 *
 * A press on commented text is not outside; it is someone asking for *that* thread.
 * It gets its own callback rather than being quietly ignored, because the caret alone
 * cannot report it: clicking the same span you just dismissed leaves the selection
 * exactly where it was, so nothing downstream would ever hear that you asked for it
 * back.
 *
 * `pointerdown` in the capture phase, so the decision is made before anything downstream
 * can stop the event, and before a drag that starts here turns into a selection.
 */
export function useCommentOutsidePress({
  enabled,
  isInside,
  onOutside,
  onCommentPress,
}: {
  enabled: boolean;
  isInside: (target: Node) => boolean;
  onOutside: () => void;
  /** A press landed on commented text: this thread is being asked for again. */
  onCommentPress: () => void;
}): void {
  const handlers = React.useRef({ isInside, onOutside, onCommentPress });

  React.useEffect(() => {
    handlers.current = { isInside, onOutside, onCommentPress };
  });

  React.useEffect(() => {
    if (!enabled) {
      return;
    }
    const handle = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (handlers.current.isInside(target)) {
        return;
      }
      if (
        target instanceof Element &&
        target.closest(PORTALLED_SURFACE_SELECTOR) !== null
      ) {
        return;
      }
      if (
        target instanceof Element &&
        target.closest(COMMENT_MARK_SELECTOR) !== null
      ) {
        handlers.current.onCommentPress();
        return;
      }
      handlers.current.onOutside();
    };
    document.addEventListener('pointerdown', handle, true);
    return () => document.removeEventListener('pointerdown', handle, true);
  }, [enabled]);
}
